<script setup>
import { motion } from "motion-v";
import { computed, ref, onMounted, watch, nextTick } from "vue";
import { useResizeListener } from "@/composables/useDevice";

const props = defineProps({
  digit: {
    type: Number,
    required: true,
  },
  height: {
    type: Number,
    default: null,
  },
  typography: {
    type: String,
    default: null,
  },
  duration: {
    type: Number,
    default: 0.5,
  },
});

const digitWindowRef = ref(null);
const actualHeight = ref(null);

// 높이를 재계산하는 함수
const updateHeight = () => {
  if (props.typography && digitWindowRef.value && process.client) {
    nextTick(() => {
      if (!digitWindowRef.value) return;
      const computedStyle = window.getComputedStyle(digitWindowRef.value);
      const newHeight = parseFloat(computedStyle.height);
      if (newHeight && newHeight !== actualHeight.value) {
        actualHeight.value = newHeight;
      }
    });
  }
};

// typography가 있을 때만 전역 resize 이벤트에 등록
useResizeListener(() => {
  if (props.typography) {
    updateHeight();
  }
});

// typography가 있으면 실제 높이를 읽어옴
onMounted(() => {
  if (!process.client) return;
  updateHeight();
});

watch(
  () => props.typography,
  () => {
    updateHeight();
  }
);

// 실제 높이 계산
const digitHeight = computed(() => {
  if (props.typography) {
    return actualHeight.value || props.height || 0;
  }
  return props.height || 0;
});

const yPosition = computed(() => {
  const h = digitHeight.value;
  if (!h) return 0;
  return -1 * props.digit * h;
});
</script>

<template>
  <div
    ref="digitWindowRef"
    class="digit-window"
    :class="{ [`digit-window--${typography}`]: typography }"
    :data-typography="typography || undefined"
    :style="height && !typography ? { height: `${height}px` } : {}"
  >
    <motion.div
      class="digit-strip"
      :key="`digit-${digit}`"
      :animate="{ y: yPosition }"
      :transition="{
        type: 'spring',
        stiffness: 60,
        damping: 12,
        mass: 1,
        bounce: 0.4,
      }"
    >
      <div
        v-for="n in 10"
        :key="n"
        class="digit-num"
        :style="
          height && !typography
            ? { height: `${height}px`, lineHeight: `${height}px` }
            : {}
        "
      >
        {{ n - 1 }}
      </div>
    </motion.div>
  </div>
</template>

<style scoped></style>
